import { gql } from '@apollo/client';

// Query to get cart order by ID
export const GET_CART_ORDER = gql`
  query GetCartOrder($orderId: String!) {
    getCartOrder(orderId: $orderId) {
      _id
      userId
      tenantId
      bundleId
      status
      items {
        _id
        itemId
        eventId
        eventTicketId
        membershipOrderId
        type
        name
        unitPrice
        discountedPrice
        quantity
        isOnSale
        saleLabel
        subTotal
        total
        checkoutAttendeesDetails
      }
      orderSummary {
        eventTicketTotal
        ticketDetails {
          ticketName
          quantity
          unitPrice
        }
        membershipTotal
        membershipDetails {
          membershipName
          unitPrice
        }
        discount
        discountCode
        subTotal
        total
      }
      paymentIntentId
      paymentIntentClientSecret
      paymentStatus
      pendingPayment {
        paymentIntentId
        amount
        currency
        initiatedAt
      }
      createdAt
      updatedAt
      completedAt
    }
  }
`;

// Query to get cart order by bundleId
export const GET_CART_ORDER_BY_BUNDLE_ID = gql`
  query GetCartOrderByBundleId($bundleId: String!) {
    getCartOrderByBundleId(bundleId: $bundleId) {
      _id
      userId
      tenantId
      bundleId
      status
      items {
        _id
        itemId
        membershipOrderId
        type
        name
        unitPrice
        discountedPrice
        quantity
        isOnSale
        saleLabel
        subTotal
        total
      }
      orderSummary {
        eventTicketTotal
        ticketDetails {
          ticketName
          quantity
          unitPrice
        }
        membershipTotal
        membershipDetails {
          membershipName
          unitPrice
        }
        discount
        discountCode
        subTotal
        total
      }
      paymentIntentId
      paymentStatus
      createdAt
      updatedAt
      completedAt
    }
  }
`;

// Query to get cart order by payment intent ID
export const GET_CART_ORDER_BY_PAYMENT_INTENT = gql`
  query GetCartOrderByPaymentIntent($paymentIntentId: String!) {
    getCartOrderByPaymentIntent(paymentIntentId: $paymentIntentId) {
      _id
      userId
      tenantId
      bundleId
      status
      items {
        _id
        itemId
        membershipOrderId
        type
        name
        unitPrice
        discountedPrice
        quantity
        isOnSale
        saleLabel
        subTotal
        total
      }
      orderSummary {
        eventTicketTotal
        ticketDetails {
          ticketName
          quantity
          unitPrice
        }
        membershipTotal
        membershipDetails {
          membershipName
          unitPrice
        }
        discount
        discountCode
        subTotal
        total
      }
      paymentIntentId
      paymentStatus
      createdAt
      updatedAt
      completedAt
    }
  }
`;

// Query to get cart order by membership order ID
export const GET_CART_ORDER_BY_MEMBERSHIP_ORDER_ID = gql`
  query GetCartOrderByMembershipOrderId($membershipOrderId: String!) {
    getCartOrderByMembershipOrderId(membershipOrderId: $membershipOrderId) {
      _id
      userId
      tenantId
      bundleId
      status
      items {
        _id
        itemId
        membershipOrderId
        type
        name
        unitPrice
        discountedPrice
        quantity
        isOnSale
        saleLabel
        subTotal
        total
      }
      orderSummary {
        eventTicketTotal
        ticketDetails {
          ticketName
          quantity
          unitPrice
        }
        membershipTotal
        membershipDetails {
          membershipName
          unitPrice
        }
        discount
        discountCode
        subTotal
        total
      }
      paymentIntentId
      paymentStatus
      createdAt
      updatedAt
      completedAt
    }
  }
`;

// Query to get all cart orders for the current user
export const GET_USER_CART_ORDERS = gql`
  query GetUserCartOrders($input: GetUserCartOrdersInput!) {
    getUserCartOrders(input: $input) {
      results {
        _id
        bundleId
        status
        paymentStatus
        items {
          _id
          itemId
          eventId
          eventTicketId
          membershipOrderId
          type
          name
          quantity
          unitPrice
          discountedPrice
          total
          checkoutAttendeesDetails
        }
        orderSummary {
          total
          subTotal
          discount
          discountCode
          eventTicketTotal
          membershipTotal
          ticketDetails {
            ticketName
            quantity
            unitPrice
          }
          membershipDetails {
            membershipName
            unitPrice
          }
        }
        createdAt
        updatedAt
        completedAt
        payableAmount
        receiptPdfUrl
      }
      totalCount
      hasMore
    }
  }
`;

export const GET_USER_PAYMENT_METHODS = gql`
  query GetUserPaymentMethods {
    currentUser {
      _id
      paymentMethods {
        _id
        stripeExternalId
        isDefault
        verified
        hasFailed
        canCurrentUserRemoveThisPaymentMethod
        ... on PaymentMethod_Card {
          creditCardLastFourDigits: lastFourDigits
          expirationMonth
          expirationYear
          brand
        }
        ... on PaymentMethod_UsBankAccount {
          bankAccountLastFourDigits: lastFourDigits
          bankName
        }
      }
    }
  }
`;

export const GET_CART = gql`
  query GetCart($input: GetCartInput!) {
    getCart(input: $input) {
      success
      message
      result {
        items {
          _id
          description
          discountedPrice
          discountAmount
          eventId
          eventTicketId
          otherItemId
          isOnSale
          itemId
          membershipOrderId
          name
          planOptions
          planType
          quantity
          saleLabel
          total
          type
          unitPrice
          subTotal
          checkoutAttendeesDetails
        }
        orderSummary {
          discount
          discountCode
          eventTicketTotal
          membershipTotal
          subTotal
          total
          creditAvailable
          creditApplied
          unusedAmount
          unusedAmountApplied
          creditIssued
          effectiveTotal
          membershipDetails {
            membershipName
            unitPrice
          }
          ticketDetails {
            quantity
            ticketName
            unitPrice
          }
        }
        cartMetadata {
          _id
          currentlyAppliedPromocode
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const GET_PROMO_CODE = gql`
  query GetApplicablePromoCodes {
    getApplicablePromoCodes {
      error
      message
      success
      total
      promoCodes {
        _id
        code
        description
        userFacingTitle
        visibility
        applicableItemIds
        applicableTiers {
          discountType
          discountValue
          entityType
          minQuantity
          currentQuantity
          itemsNeeded
          entityIds
        }
      }
    }
  }
`;

export const GET_CART_METADATA = gql`
  query GetCartMetadata {
    getCartMetadata {
      success
      message
      error
      cart {
        _id
        currentlyAppliedPromocode
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_CART_ITEMS_FOR_EVENT = gql`
  query GetCartItemsForEvent($eventId: String!) {
    getCartItemsForEvent(eventId: $eventId) {
      success
      message
      error
      result {
        items {
          _id
          itemId
          eventId
          eventTicketId
          membershipOrderId
          type
          quantity
          checkoutAttendeesDetails
        }
        orderSummary {
          eventTicketTotal
          ticketDetails {
            ticketName
            unitPrice
            quantity
          }
          membershipTotal
          discount
          discountCode
          subTotal
          total
        }
      }
    }
  }
`;

// Subscription for cart order processing completion notifications
export const CART_ORDER_PROCESSING_COMPLETE_SUBSCRIPTION = gql`
  subscription CartOrderProcessingComplete($input: CartOrderSubscriptionInput) {
    cartOrderProcessingComplete(input: $input) {
      message
      orderId
      timestamp
      userId
    }
  }
`;

export const CART_ORDER_UPDATES_SUBSCRIPTION = gql`
  subscription CartOrderUpdates($input: CartOrderSubscriptionInput) {
    cartOrderUpdates(input: $input) {
      userId
      orderId
      bundleId
      eventType
      timestamp
      itemId
      itemType
      message
    }
  }
`;
